import React, { useState, useEffect, useRef } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import CopyrightIcon from '@mui/icons-material/Copyright';
import Select from 'react-select';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import '../shared.css';
import LoadingOverlay from '../LoadingOverlay';

function OtherStockTransferOut() {
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();
    const [totalqty, setTotalqty] = useState(0);
    const [branchOptions, setBranchOptions] = useState([]);
    const [selectedBranches, setSelectedBranches] = useState([]);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
        const startDate = getFormattedStartDate();
        const currentDate = getFormattedCurrentDate();
        setFromDate(startDate);
        setToDate(currentDate);
        fetchBranchData();
    }, [isAuthenticated, companyId, branchId, token, logout]);

    useEffect(() => {
        if (fromDate && toDate) {
            fetchTableData(fromDate, toDate);
        }
    }, [fromDate, toDate]);

    const handleBranchSelect = (opts) => { setSelectedBranches(opts); setDataItem([]); };

    const fetchBranchData = () => {
        fetchData('ReportApi', `/Branch1/${companyId}/${branchId}`, token)
            .then(r => setBranchOptions(r.response_data.map(c => ({ label: c.Name, value: c.Id }))))
            .catch(console.error);
    };

    useEffect(() => { let qty = 0; dataItem.forEach(item => { qty += item.Quantity; }); setTotalqty(qty); }, [dataItem]);

    const getFormattedCurrentDate = () => new Date().toISOString().split('T')[0];
    const getFormattedStartDate = () => { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0]; };
    const handleFromDateChange = (e) => setFromDate(e.target.value);
    const handleToDateChange = (e) => setToDate(e.target.value);
    const hasReadReportPermission = permissions.includes('Read Reports');
    const [loading, setLoading] = useState(false);
    const loadingRef = useRef(false);

    const fetchTableData = async (fDate, tDate) => {
        const effectiveFromDate = fDate || fromDate;
        const effectiveToDate = tDate || toDate;
        if (hasReadReportPermission && !loadingRef.current && effectiveFromDate && effectiveToDate) {
            setDataItem([]); setLoading(true);
            loadingRef.current = true;
            const branchIds = selectedBranches.length === 0 ? branchOptions.map(b => b.value) : selectedBranches.map(b => b.value);
            try {
                const response = await fetchData('OtherReportApi', `/StockInSummaryOut/${companyId}/${branchIds.join(',')}/${effectiveFromDate}/${effectiveToDate}`, token);
                if (response.responseCode === '1111') setDataItem(response.response_data);
            } catch (e) { console.error(e); } finally { loadingRef.current = false; setLoading(false); }
        }
    };

    const handleSearch = (e) => setSearchQuery(e.target.value);
    const filteredDataItem = dataItem.filter(item => Object.values(item).some(v => String(v).toLowerCase().includes(searchQuery.toLowerCase())));
    const formatDate = (d) => new Date(d).toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' });

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Stock Transfer Out Summary');
        const rightAlignColumns = ['Quantity','MRP','GST %','Discount %'];
        worksheet.mergeCells('A1:AM1');
        const hc = worksheet.getCell('A1'); hc.value = `Stock Transfer Out Summary From : ${formatDate(fromDate)} To : ${formatDate(toDate)}`; hc.font = { bold: true, size: 14 }; hc.alignment = { vertical: 'middle', horizontal: 'left' };
        const headerRowValues = ['Source Store','Stock out Date','Stock Out Time','Destination Store','Stock In Date','Stock  In Time','Item No','Description','Quantity','MRP','GST %','HSN Code','Discount %','PG','IG','ISG','PF','Category','Group','Season Code','Size Code'];
        const headerRow = worksheet.addRow(headerRowValues);
        headerRow.eachCell(cell => { cell.fill = { type:'pattern',pattern:'solid',fgColor:{argb:'FFB6D7A8'} }; cell.font = { bold:true,color:{argb:'FF000000'} }; cell.alignment = { vertical:'middle',horizontal:'left',wrapText:true }; cell.border = { top:{style:'thin'},left:{style:'thin'},bottom:{style:'thin'},right:{style:'thin'} }; });
        worksheet.columns = headerRowValues.map(() => ({ width: 15 }));
        filteredDataItem.forEach(rowData => {
            const row = worksheet.addRow([rowData.SourceStore,formatDate(rowData.StockoutDate),rowData.StockOutTime,rowData.DestinationStore,rowData.StockInDate?formatDate(rowData.StockInDate):'',rowData.StockInTime,rowData.Code,rowData.Name,parseFloat(rowData.Quantity),parseFloat(rowData.MRP.toFixed(2)),parseFloat(rowData.GSTRate),rowData.HSNCode,rowData.DiscountPercentage,rowData.PG,rowData.IG,rowData.ISG,rowData.PF,rowData.CategoryName,rowData.ItemGroupName,rowData.SeasonCode,rowData.SizeCode]);
            row.eachCell((cell, col) => { cell.alignment = { vertical:'middle',horizontal:rightAlignColumns.includes(headerRowValues[col-1])?'right':'center' }; cell.border = { top:{style:'thin'},left:{style:'thin'},bottom:{style:'thin'},right:{style:'thin'} }; });
        });
        const totalRow = worksheet.addRow(['','','','','','','','Total',totalqty,'','','','','','','','','','','','']);
        totalRow.font = { bold: true };
        totalRow.eachCell((cell, col) => { cell.alignment = { vertical:'middle',horizontal:rightAlignColumns.includes(headerRowValues[col-1])?'right':'center' }; cell.fill = { type:'pattern',pattern:'solid',fgColor:{argb:'FFFFF2CC'} }; cell.border = { top:{style:'thin'},left:{style:'thin'},bottom:{style:'thin'},right:{style:'thin'} }; });
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), 'StockTransferOut_Summary.xlsx');
    };

    const branchOptionsWithBlank = [{ label: '--Select--', value: 0 }, ...branchOptions];

    return (
        <div className="pg-container">
            <div className="pg-header">
                <h5 className="pg-title">Stock Transfer Out Summary</h5>
                <div className="pg-header-actions">
                    <div className="pg-date-row">
                        <div className="pg-field"><label className="pg-label" style={{ background: 'var(--pg-bg)' }}>From Date</label><input className="pg-input" type="date" value={fromDate} onChange={handleFromDateChange} /></div>
                        <div className="pg-field"><label className="pg-label" style={{ background: 'var(--pg-bg)' }}>To Date</label><input className="pg-input" type="date" value={toDate} onChange={handleToDateChange} /></div>
                        <div className="pg-field" style={{ minWidth: 220 }}>
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>Branch <span style={{ color: 'red' }}>*</span></label>
                            <Select options={branchOptionsWithBlank} value={selectedBranches} onChange={handleBranchSelect} placeholder="Select Branch" isMulti menuPortalTarget={document.body} menuPosition="fixed" styles={{ control: b => ({ ...b, minHeight: 31, height: 31, fontSize: 12.5, borderColor: 'rgba(200,212,224,0.7)', borderRadius: 6 }), valueContainer: b => ({ ...b, height: 31, padding: '0 6px' }), indicatorsContainer: b => ({ ...b, height: 31 }), menu: b => ({ ...b, fontSize: 12.5 }), menuPortal: b => ({ ...b, zIndex: 9999 }) }} />
                        </div>
                        <button className="pg-btn pg-btn-outline" onClick={() => fetchTableData()} disabled={loading}><RefreshIcon /> {loading ? 'Loading...' : 'Refresh'}</button>
                        <button className="pg-btn pg-btn-success" onClick={handleExportExcel}><FeedIcon /> Excel</button>
                        <button className="pg-btn pg-btn-outline" onClick={() => setShowSearchInput(v => !v)}><SearchIcon /> Search</button>
                    </div>
                    {showSearchInput && <input className="pg-input pg-search-input" placeholder="Search…" value={searchQuery} onChange={handleSearch} autoFocus style={{ marginTop: 6 }} />}
                </div>
            </div>
            {loading && <LoadingOverlay />}
            <div className="pg-table-wrap">
                <table className="pg-table">
                    <thead><tr><th>Source Store</th><th>Stock out Date</th><th>Stock Out Time</th><th>Destination Store</th><th>Stock In Date</th><th>Stock In Time</th><th>Item No</th><th>Description</th><th>Quantity</th><th>MRP</th><th>GST %</th><th>HSN Code</th><th>Discount %</th><th>PG</th><th>IG</th><th>ISG</th><th>PF</th><th>Category</th><th>Group</th><th>Season Code</th><th>Size Code</th></tr></thead>
                    <tbody>
                        {filteredDataItem.map((data, i) => (
                            <tr key={i}><td>{data.SourceStore}</td><td>{formatDate(data.StockoutDate)}</td><td>{data.StockOutTime}</td><td>{data.DestinationStore}</td><td>{data.StockInDate?formatDate(data.StockInDate):''}</td><td>{data.StockInTime}</td><td>{data.Code}</td><td>{data.Name}</td><td>{data.Quantity}</td><td>{data.MRP.toFixed(2)}</td><td>{data.GSTRate}</td><td>{data.HSNCode}</td><td>{data.DiscountPercentage}</td><td>{data.PG}</td><td>{data.IG}</td><td>{data.ISG}</td><td>{data.PF}</td><td>{data.CategoryName}</td><td>{data.ItemGroupName}</td><td>{data.SeasonCode}</td><td>{data.SizeCode}</td></tr>
                        ))}
                        <tr style={{ background: '#1c2a3a', color: '#fff', fontWeight: 700 }}>
                            <td colSpan={7}></td><td>Total</td><td>{totalqty}</td><td colSpan={12}></td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
        </div>
    );
}

export default OtherStockTransferOut;
